/**
 * Character Validation Service
 *
 * Validates character data on import using Zod schemas.
 * Checks for missing purchaseHistory, incomplete data, and invalid IDs.
 * Displays warnings for invalid data but allows import to proceed with defaults.
 *
 * Requirements: 2.4, 8.5
 * Validates: Property 13 (Schema Validation)
 */

import type { Character, Skill, Talent, SkillPurchase, TalentPurchase } from '../types/character';
import { SkillCategory, SkillTraining } from '../types/character';

/**
 * Result of character validation
 */
export interface CharacterValidationResult {
  /** Whether the character data is valid enough to use */
  isValid: boolean;
  /** Critical errors that prevent character use */
  errors: string[];
  /** Non-critical warnings about data quality */
  warnings: string[];
  /** The character data with defaults applied for missing fields */
  character: Character | null;
  /** Details about what was fixed/defaulted */
  fixes: string[];
}

/**
 * Result of validating a single skill
 */
interface SkillValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  skill: Skill;
  fixes: string[];
}

/**
 * Result of validating a single talent
 */
interface TalentValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  talent: Talent;
  fixes: string[];
}

/**
 * Kebab-case pattern for ID validation
 */
const KEBAB_CASE_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/**
 * Character Validation Service
 * Validates character data and applies defaults for missing fields.
 */
class CharacterValidationService {
  private static instance: CharacterValidationService;

  public static getInstance(): CharacterValidationService {
    if (!CharacterValidationService.instance) {
      CharacterValidationService.instance = new CharacterValidationService();
    }
    return CharacterValidationService.instance;
  }

  /**
   * Validate character data on import.
   * Checks for missing purchaseHistory, incomplete data, and invalid IDs.
   * Returns warnings but allows import to proceed with defaults.
   *
   * Requirements: 2.4, 8.5
   *
   * @param characterData - Raw character data to validate
   * @returns Validation result with character data and any warnings/errors
   */
  public validateCharacterOnImport(characterData: unknown): CharacterValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    const fixes: string[] = [];

    // Check if data is an object
    if (!characterData || typeof characterData !== 'object') {
      return {
        isValid: false,
        errors: ['Character data must be a valid object'],
        warnings: [],
        character: null,
        fixes: []
      };
    }

    const data = characterData as Record<string, unknown>;

    // Check required top-level fields
    if (!data.id || typeof data.id !== 'string') {
      errors.push('Character ID is required');
    }

    if (!data.name || typeof data.name !== 'string') {
      errors.push('Character name is required');
    }

    // Validate and fix skills
    const skillsResult = this.validateAndFixSkills(data.skills as unknown[]);
    warnings.push(...skillsResult.warnings);
    errors.push(...skillsResult.errors);
    fixes.push(...skillsResult.fixes);

    // Validate and fix talents
    const talentsResult = this.validateAndFixTalents(data.talents as unknown[]);
    warnings.push(...talentsResult.warnings);
    errors.push(...talentsResult.errors);
    fixes.push(...talentsResult.fixes);

    // Check for missing required fields and add warnings
    this.checkRequiredFields(data, warnings);

    // Build the validated character with fixes applied
    const character: Character = {
      ...(data as unknown as Character),
      skills: skillsResult.skills,
      talents: talentsResult.talents
    };

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      character,
      fixes
    };
  }

  /**
   * Validate and fix skills array.
   * Initializes missing purchaseHistory arrays and validates IDs.
   *
   * Requirements: 2.4, 3.1, 5.5
   */
  private validateAndFixSkills(skillsData: unknown[]): {
    skills: Skill[];
    errors: string[];
    warnings: string[];
    fixes: string[];
  } {
    const errors: string[] = [];
    const warnings: string[] = [];
    const fixes: string[] = [];
    const skills: Skill[] = [];

    if (!Array.isArray(skillsData)) {
      warnings.push('Skills array is missing or invalid, initializing as empty array');
      return { skills: [], errors, warnings, fixes: ['Initialized empty skills array'] };
    }

    for (let i = 0; i < skillsData.length; i++) {
      const result = this.validateAndFixSkill(skillsData[i], i);
      errors.push(...result.errors);
      warnings.push(...result.warnings);
      fixes.push(...result.fixes);
      skills.push(result.skill);
    }

    return { skills, errors, warnings, fixes };
  }

  /**
   * Validate and fix a single skill.
   * Initializes missing purchaseHistory and validates ID format.
   *
   * Requirements: 2.4, 3.1, 5.5
   */
  private validateAndFixSkill(skillData: unknown, index: number): SkillValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    const fixes: string[] = [];

    if (!skillData || typeof skillData !== 'object') {
      return {
        isValid: false,
        errors: [`Skill at index ${index} is not a valid object`],
        warnings: [],
        skill: this.createDefaultSkill(`unknown-${index}`),
        fixes: []
      };
    }

    const data = skillData as Record<string, unknown>;
    const skillId = (data.id as string) || `unknown-skill-${index}`;
    const skillName = (data.name as string) || skillId;

    // Validate ID format (kebab-case)
    if (data.id && typeof data.id === 'string') {
      if (!KEBAB_CASE_PATTERN.test(data.id)) {
        warnings.push(`Skill "${skillName}" has non-kebab-case ID: ${data.id}`);
      }
    } else {
      warnings.push(`Skill at index ${index} missing string ID`);
    }

    // Check and fix purchaseHistory
    let purchaseHistory: SkillPurchase[] = [];
    if (!Array.isArray(data.purchaseHistory)) {
      warnings.push(`Skill "${skillName}" missing purchaseHistory array`);
      fixes.push(`Initialized empty purchaseHistory for skill "${skillName}"`);
      purchaseHistory = [];
    } else {
      // Validate each purchase in history
      const purchaseResult = this.validateAndFixSkillPurchaseHistory(
        data.purchaseHistory as unknown[],
        skillName
      );
      warnings.push(...purchaseResult.warnings);
      fixes.push(...purchaseResult.fixes);
      purchaseHistory = purchaseResult.purchases;
    }

    // Check required fields
    if (!data.name || typeof data.name !== 'string') {
      warnings.push(`Skill "${skillId}" missing name`);
    }

    if (!data.characteristic) {
      warnings.push(`Skill "${skillName}" missing characteristic`);
    }

    if (!data.category) {
      warnings.push(`Skill "${skillName}" missing category`);
    }

    if (!data.rulebook) {
      warnings.push(`Skill "${skillName}" missing rulebook`);
    }

    // Build the fixed skill
    const skill: Skill = {
      ...(data as unknown as Skill),
      id: skillId,
      purchaseHistory
    };

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      skill,
      fixes
    };
  }

  /**
   * Validate and fix skill purchase history.
   * Ensures all required fields are present.
   *
   * Requirements: 3.1, 3.3
   */
  private validateAndFixSkillPurchaseHistory(
    historyData: unknown[],
    skillName: string
  ): {
    purchases: SkillPurchase[];
    warnings: string[];
    fixes: string[];
  } {
    const warnings: string[] = [];
    const fixes: string[] = [];
    const purchases: SkillPurchase[] = [];

    for (let i = 0; i < historyData.length; i++) {
      const purchaseData = historyData[i] as Record<string, unknown>;

      if (!purchaseData || typeof purchaseData !== 'object') {
        warnings.push(`Skill "${skillName}" purchase ${i} is not a valid object`);
        continue;
      }

      // Check required fields
      if (!purchaseData.advancementId) {
        warnings.push(`Skill "${skillName}" purchase ${i} missing advancementId`);
      }

      if (typeof purchaseData.rank !== 'number') {
        warnings.push(`Skill "${skillName}" purchase ${i} missing rank`);
      }

      if (typeof purchaseData.bonus !== 'number') {
        warnings.push(`Skill "${skillName}" purchase ${i} missing bonus`);
      } else if (purchaseData.bonus !== 10 && purchaseData.bonus !== 20) {
        warnings.push(
          `Skill "${skillName}" purchase ${i} has invalid bonus: ${purchaseData.bonus} (should be 10 or 20)`
        );
      }

      // Build the purchase with defaults
      const purchase: SkillPurchase = {
        advancementId: (purchaseData.advancementId as string) || `unknown-advancement-${i}`,
        purchasedAt:
          purchaseData.purchasedAt instanceof Date
            ? purchaseData.purchasedAt
            : new Date(purchaseData.purchasedAt as string),
        rank: (purchaseData.rank as number) || 1,
        bonus: (purchaseData.bonus as number) || 10
      };

      purchases.push(purchase);
    }

    return { purchases, warnings, fixes };
  }

  /**
   * Validate and fix talents array.
   * Initializes missing purchaseHistory arrays and validates IDs.
   *
   * Requirements: 2.4, 3.2, 5.6
   */
  private validateAndFixTalents(talentsData: unknown[]): {
    talents: Talent[];
    errors: string[];
    warnings: string[];
    fixes: string[];
  } {
    const errors: string[] = [];
    const warnings: string[] = [];
    const fixes: string[] = [];
    const talents: Talent[] = [];

    if (!Array.isArray(talentsData)) {
      warnings.push('Talents array is missing or invalid, initializing as empty array');
      return { talents: [], errors, warnings, fixes: ['Initialized empty talents array'] };
    }

    for (let i = 0; i < talentsData.length; i++) {
      const result = this.validateAndFixTalent(talentsData[i], i);
      errors.push(...result.errors);
      warnings.push(...result.warnings);
      fixes.push(...result.fixes);
      talents.push(result.talent);
    }

    return { talents, errors, warnings, fixes };
  }

  /**
   * Validate and fix a single talent.
   * Initializes missing purchaseHistory and validates ID format.
   * Special handling for "Talented" talent affectedSkillId.
   *
   * Requirements: 2.4, 3.2, 4.2, 5.6
   */
  private validateAndFixTalent(talentData: unknown, index: number): TalentValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    const fixes: string[] = [];

    if (!talentData || typeof talentData !== 'object') {
      return {
        isValid: false,
        errors: [`Talent at index ${index} is not a valid object`],
        warnings: [],
        talent: this.createDefaultTalent(`unknown-${index}`),
        fixes: []
      };
    }

    const data = talentData as Record<string, unknown>;
    const talentId = (data.id as string) || `unknown-talent-${index}`;
    const talentName = (data.name as string) || talentId;

    // Validate ID format (kebab-case)
    if (data.id && typeof data.id === 'string') {
      if (!KEBAB_CASE_PATTERN.test(data.id)) {
        warnings.push(`Talent "${talentName}" has non-kebab-case ID: ${data.id}`);
      }
    } else {
      warnings.push(`Talent at index ${index} missing string ID`);
    }

    // Check and fix purchaseHistory
    let purchaseHistory: TalentPurchase[] = [];
    if (!Array.isArray(data.purchaseHistory)) {
      warnings.push(`Talent "${talentName}" missing purchaseHistory array`);
      fixes.push(`Initialized empty purchaseHistory for talent "${talentName}"`);
      purchaseHistory = [];
    } else {
      // Validate each purchase in history
      const purchaseResult = this.validateAndFixTalentPurchaseHistory(
        data.purchaseHistory as unknown[],
        talentName,
        talentId
      );
      warnings.push(...purchaseResult.warnings);
      fixes.push(...purchaseResult.fixes);
      purchaseHistory = purchaseResult.purchases;
    }

    // Check and fix purchaseCount
    let purchaseCount = data.purchaseCount as number;
    if (typeof purchaseCount !== 'number') {
      purchaseCount = purchaseHistory.length;
      if (purchaseHistory.length > 0) {
        fixes.push(`Set purchaseCount to ${purchaseCount} for talent "${talentName}"`);
      }
    }

    // Check required fields
    if (!data.name || typeof data.name !== 'string') {
      warnings.push(`Talent "${talentId}" missing name`);
    }

    if (!data.rulebook) {
      warnings.push(`Talent "${talentName}" missing rulebook`);
    }

    // Special validation for "Talented" talent
    if (talentId === 'talented' || talentId.startsWith('talented-')) {
      const hasAffectedSkillId =
        data.affectedSkillId || purchaseHistory.some((p) => p.affectedSkillId);

      if (!hasAffectedSkillId && purchaseHistory.length > 0) {
        warnings.push(`Talented talent "${talentName}" missing affectedSkillId`);
      }
    }

    // Build the fixed talent
    const talent: Talent = {
      ...(data as unknown as Talent),
      id: talentId,
      purchaseHistory,
      purchaseCount
    };

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      talent,
      fixes
    };
  }

  /**
   * Validate and fix talent purchase history.
   * Ensures all required fields are present.
   * Special handling for "Talented" talent affectedSkillId.
   *
   * Requirements: 3.2, 3.3, 4.2
   */
  private validateAndFixTalentPurchaseHistory(
    historyData: unknown[],
    talentName: string,
    talentId: string
  ): {
    purchases: TalentPurchase[];
    warnings: string[];
    fixes: string[];
  } {
    const warnings: string[] = [];
    const fixes: string[] = [];
    const purchases: TalentPurchase[] = [];
    const isTalentedTalent = talentId === 'talented' || talentId.startsWith('talented-');

    for (let i = 0; i < historyData.length; i++) {
      const purchaseData = historyData[i] as Record<string, unknown>;

      if (!purchaseData || typeof purchaseData !== 'object') {
        warnings.push(`Talent "${talentName}" purchase ${i} is not a valid object`);
        continue;
      }

      // Check required fields
      if (!purchaseData.advancementId) {
        warnings.push(`Talent "${talentName}" purchase ${i} missing advancementId`);
      }

      if (typeof purchaseData.rank !== 'number') {
        warnings.push(`Talent "${talentName}" purchase ${i} missing rank`);
      }

      // Special check for "Talented" talent
      if (isTalentedTalent && !purchaseData.affectedSkillId) {
        warnings.push(`Talented talent "${talentName}" purchase ${i} missing affectedSkillId`);
      }

      // Build the purchase with defaults
      const purchase: TalentPurchase = {
        advancementId: (purchaseData.advancementId as string) || `unknown-advancement-${i}`,
        purchasedAt:
          purchaseData.purchasedAt instanceof Date
            ? purchaseData.purchasedAt
            : new Date(purchaseData.purchasedAt as string),
        rank: (purchaseData.rank as number) || 1
      };

      // Preserve affectedSkillId for "Talented" talent
      if (purchaseData.affectedSkillId) {
        purchase.affectedSkillId = purchaseData.affectedSkillId as string;
      }

      purchases.push(purchase);
    }

    return { purchases, warnings, fixes };
  }

  /**
   * Check for missing required fields and add warnings.
   */
  private checkRequiredFields(data: Record<string, unknown>, warnings: string[]): void {
    // Check career
    if (!data.career || typeof data.career !== 'object') {
      warnings.push('Character missing career data');
    }

    // Check homeworld
    if (!data.homeworld || typeof data.homeworld !== 'object') {
      warnings.push('Character missing homeworld data');
    }

    // Check characteristics
    if (!data.characteristics || typeof data.characteristics !== 'object') {
      warnings.push('Character missing characteristics data');
    }

    // Check experience
    if (!data.experience || typeof data.experience !== 'object') {
      warnings.push('Character missing experience data');
    }

    // Check derivedStats
    if (!data.derivedStats || typeof data.derivedStats !== 'object') {
      warnings.push('Character missing derivedStats data');
    }

    // Check schemaVersion
    if (typeof data.schemaVersion !== 'number') {
      warnings.push('Character missing schemaVersion');
    }
  }

  /**
   * Create a default skill with minimal required fields.
   */
  private createDefaultSkill(id: string): Skill {
    return {
      id,
      name: id,
      characteristic: 'intelligence',
      category: SkillCategory.Knowledge,
      training: SkillTraining.Untrained,
      isSpecialist: false,
      rulebook: 'rogue-trader-core',
      purchaseHistory: []
    };
  }

  /**
   * Create a default talent with minimal required fields.
   */
  private createDefaultTalent(id: string): Talent {
    return {
      id,
      name: id,
      tier: 1,
      prerequisites: [],
      shortDescription: '',
      fullDescription: '',
      rulebook: 'rogue-trader-core',
      purchaseCount: 0,
      purchaseHistory: []
    };
  }

  /**
   * Log validation warnings to console.
   * Used for debugging and development.
   */
  public logValidationResult(result: CharacterValidationResult): void {
    if (result.errors.length > 0) {
      console.error('Character validation errors:', result.errors);
    }

    if (result.warnings.length > 0) {
      console.warn('Character validation warnings:', result.warnings);
    }

    if (result.fixes.length > 0) {
      console.info('Character data fixes applied:', result.fixes);
    }
  }
}

export const characterValidationService = CharacterValidationService.getInstance();
